import React from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Chip, FormControl, FormHelperText, Grid,
  InputLabel, MenuItem, Select, Stack, TextField, Typography
} from "@mui/material";
import {grey} from "@mui/material/colors";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Mic01Icon, VolumeHighIcon} from "@hugeicons/core-free-icons";
import {AUDIO_ENCODINGS, GEMINI_VOICE_NAMES} from "./voiceOptions";

// Default voice / transcribe configuration for a newly selected language. Language codes default to the
// language's code, and a language with exactly one transcribe model gets it preselected.
export const createLanguageConfig = (language, transcribeModels) => {
  // language: a catalog language, or a sub project item whose languageId differs from its id
  const models = (transcribeModels || []).filter(model => model.languageId === (language?.languageId ?? language?.id));
  return {
    voiceConfiguration: {
      voiceModelId: null,
      voice: "",
      languageCode: language?.code || "",
      audioEncoding: "LINEAR16",
      pitch: 0,
      speakingRate: 1,
    },
    transcribeConfiguration: {
      transcribeModelId: models.length === 1 ? models[0].id : null,
      languageCode: language?.code || "",
    },
    // delivery guidance for the voice, stored only in the Firebase flow
    prompt: "",
  };
};

// Voice / transcribe configuration of one language as sent to the API
export const toApiConfiguration = (config) => ({
  voiceConfiguration: {
    voiceModelId: config.voiceConfiguration.voiceModelId || null,
    voice: config.voiceConfiguration.voice || "",
    languageCode: config.voiceConfiguration.languageCode || "",
    audioEncoding: config.voiceConfiguration.audioEncoding || "LINEAR16",
    pitch: config.voiceConfiguration.pitch ?? 0,
    speakingRate: config.voiceConfiguration.speakingRate ?? 1,
  },
  transcribeConfiguration: {
    transcribeModelId: config.transcribeConfiguration.transcribeModelId || null,
    languageCode: config.transcribeConfiguration.languageCode || "",
  },
});

// The flow runtime reads the voice from the Firebase settings, in the Gemini TTS shape
export const toVoiceGemini = (config, voiceModels) => {
  const voice = config?.voiceConfiguration;
  if (!voice?.voiceModelId) return null;
  const voiceModel = voiceModels.find(model => model.id === voice.voiceModelId);
  return {
    voice: {modelName: voiceModel?.modelId || null, name: voice.voice || "", languageCode: voice.languageCode || ""},
    audioConfig: {audioEncoding: voice.audioEncoding || "LINEAR16", pitch: voice.pitch ?? 0, speakingRate: voice.speakingRate ?? 1},
    input: {prompt: config.prompt || ""},
  };
};

export const SubHeader = ({icon, title}) => (
  <Stack direction={"row"} gap={1} alignItems={"center"} sx={{mb: 1.5, color: "text.secondary"}}>
    <HugeiconsIcon icon={icon} size={16}/>
    <Typography fontWeight={600} fontSize={"0.85rem"}>{title}</Typography>
  </Stack>
);

// One collapsible voice + transcribe configuration per selected language.
// renderExtra / renderAfter(languageId, config) add fields above the voice configuration and below the
// transcribe configuration (status and core configuration in project settings).
export const LanguageConfigurations = ({
  languageIds, languages, configs, voiceModels, transcribeModels, onChange, fieldSx, selectLabelSx, renderExtra,
  renderAfter
}) => {
  if (!languageIds?.length) return null;

  return (
    <Stack direction={"column"} gap={1.5} sx={{mt: 2}}>
      {languageIds.map((languageId, index) => {
        const language = languages.find(item => item.id === languageId);
        const config = configs?.[languageId];
        if (!config) return null;

        const {voiceConfiguration: voice, transcribeConfiguration: transcribe} = config;
        // keyed by sub project id in project settings, so the transcribe models follow the item's own language
        const languageModels = (transcribeModels || []).filter(model => model.languageId === (language?.languageId ?? languageId));
        const voiceModelName = voiceModels?.find(model => model.id === voice.voiceModelId)?.name;
        const transcribeModelName = languageModels.find(model => model.id === transcribe.transcribeModelId)?.name;

        const update = (section, key, value) => onChange(languageId, {
          ...config,
          [section]: {...config[section], [key]: value},
        });
        const toNumber = (value) => value === "" ? null : Number(value);

        return (
          <Accordion
            key={languageId}
            defaultExpanded={index === 0}
            disableGutters
            elevation={0}
            sx={{border: 1, borderColor: grey[200], borderRadius: "12px !important", "&:before": {display: "none"}}}
          >
            <AccordionSummary expandIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={18}/>}>
              <Stack direction={{xs: "column", sm: "row"}} gap={{xs: 0.5, sm: 1.5}} alignItems={{sm: "center"}} sx={{minWidth: 0}}>
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <Typography fontWeight={600}>{language?.language || "Language"}</Typography>
                  {language?.code && <Chip label={language.code} size={"small"} sx={{bgcolor: grey[100]}}/>}
                </Stack>
                <Typography variant={"body2"} color={"text.secondary"} noWrap>
                  Voice: {voiceModelName || "Default"} · Transcribe: {transcribeModelName || "Default"}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{pt: 0}}>
              {renderExtra && <Box sx={{mb: 3}}>{renderExtra(languageId, config)}</Box>}
              <Box sx={{mb: 3}}>
                <SubHeader icon={VolumeHighIcon} title={"Voice Configuration"}/>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id={`voice-model-${languageId}`} shrink sx={selectLabelSx}>Voice Model</InputLabel>
                      <Select
                        labelId={`voice-model-${languageId}`}
                        label={"Voice Model"}
                        value={voice.voiceModelId ?? ""}
                        displayEmpty
                        sx={fieldSx}
                        onChange={e => update("voiceConfiguration", "voiceModelId", e.target.value || null)}
                      >
                        <MenuItem value={""}><em>Default Model</em></MenuItem>
                        {voiceModels?.map(model => (
                          <MenuItem key={model.id} value={model.id}>{model.name || model.modelId}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={GEMINI_VOICE_NAMES}
                      inputValue={voice.voice || ""}
                      onInputChange={(e, value) => update("voiceConfiguration", "voice", value)}
                      renderInput={(props) => (
                        <TextField {...props} label={"Voice"} sx={fieldSx} InputLabelProps={{shrink: true}}/>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={"Voice Language Code"}
                      value={voice.languageCode || ""}
                      onChange={e => update("voiceConfiguration", "languageCode", e.target.value)}
                      helperText={`Defaults to the language code (${language?.code || "-"})`}
                      fullWidth
                      sx={fieldSx}
                      InputLabelProps={{shrink: true}}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id={`audio-encoding-${languageId}`} shrink sx={selectLabelSx}>Audio Encoding</InputLabel>
                      <Select
                        labelId={`audio-encoding-${languageId}`}
                        label={"Audio Encoding"}
                        value={voice.audioEncoding || "LINEAR16"}
                        sx={fieldSx}
                        onChange={e => update("voiceConfiguration", "audioEncoding", e.target.value)}
                      >
                        {AUDIO_ENCODINGS.map(encoding => <MenuItem key={encoding} value={encoding}>{encoding}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={"Pitch"}
                      type={"number"}
                      helperText={"-20.0 to 20.0"}
                      inputProps={{step: 0.1, min: -20, max: 20}}
                      value={voice.pitch ?? ""}
                      onChange={e => update("voiceConfiguration", "pitch", toNumber(e.target.value))}
                      fullWidth
                      sx={fieldSx}
                      InputLabelProps={{shrink: true}}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={"Speaking Rate"}
                      type={"number"}
                      helperText={"0.25 to 2.0"}
                      inputProps={{step: 0.05, min: 0.25, max: 2}}
                      value={voice.speakingRate ?? ""}
                      onChange={e => update("voiceConfiguration", "speakingRate", toNumber(e.target.value))}
                      fullWidth
                      sx={fieldSx}
                      InputLabelProps={{shrink: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={"Style Prompt"}
                      placeholder={"e.g. Speak in a calm, friendly tone."}
                      helperText={"Optional delivery guidance, supports tags like [whispering], [laughing]"}
                      value={config.prompt || ""}
                      onChange={e => onChange(languageId, {...config, prompt: e.target.value})}
                      fullWidth
                      multiline
                      rows={2}
                      sx={fieldSx}
                      InputLabelProps={{shrink: true}}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <SubHeader icon={Mic01Icon} title={"Transcribe Configuration"}/>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id={`transcribe-model-${languageId}`} shrink sx={selectLabelSx}>Transcribe Model</InputLabel>
                      <Select
                        labelId={`transcribe-model-${languageId}`}
                        label={"Transcribe Model"}
                        value={transcribe.transcribeModelId ?? ""}
                        displayEmpty
                        sx={fieldSx}
                        onChange={e => update("transcribeConfiguration", "transcribeModelId", e.target.value || null)}
                      >
                        <MenuItem value={""}><em>Default Model</em></MenuItem>
                        {languageModels.map(model => (
                          <MenuItem key={model.id} value={model.id}>{model.name || model.modelId}</MenuItem>
                        ))}
                      </Select>
                      {languageModels.length === 0 && (
                        <FormHelperText>No transcribe models available for {language?.language || "this language"}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={"Transcribe Language Code"}
                      value={transcribe.languageCode || ""}
                      onChange={e => update("transcribeConfiguration", "languageCode", e.target.value)}
                      helperText={`Defaults to the language code (${language?.code || "-"})`}
                      fullWidth
                      sx={fieldSx}
                      InputLabelProps={{shrink: true}}
                    />
                  </Grid>
                </Grid>
              </Box>
              {renderAfter && <Box sx={{mt: 3}}>{renderAfter(languageId, config)}</Box>}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};
